import {type FC, useEffect, useRef, useState} from 'react';
import LogoSVG from '../../assets/common/logo-sm.svg';
import Logo from '../../compoents/Logo.tsx';
import useCenterStore from '../../store/useCenterStore.ts';
import Package from '../../../package.json';
import {
    Button,
    ButtonGroup,
    Container,
    DeviceSelector,
    EditableRow,
    Header,
    IconArea,
    InfoRow,
    Partition,
    SettingArea,
    ToggleButton,
    ToggleContainer,
    VolumeControl,
    VolumeLabel,
    VolumeSlider,
} from './types/InformationSetting.styled.ts';
import type {CenterInfo} from '../../models';
import type {IVideoMode} from './types';

const InformationSetting: FC = () => {
    const {
        deviceID,
        centerInfo,
        allDevices,
        setAllDevices,
        setCaptureBoardMediaStream,
        videoInputA,
        audioInputA,
        audioOutputA,
        setMediaStream,
        setDatas,
        videoInputD,
        audioInputD,
        audioOutputD,
        captureBoardD,
        captureBoardAudioD,
        videoMode,
        setVideoMode,
        cameraEnable,
        setToggleCameraEnable,
        electronVer
    } = useCenterStore();
    const [editableInfo, setEditableInfo] = useState<CenterInfo | null>(centerInfo);
    const [isEditing, setIsEditing] = useState(false);

    const [selectedVideoMode, setSelectedVideoMode] = useState<IVideoMode>(videoMode ?? 'best-perform');
    const [selectedVideoInput, setSelectedVideoInput] = useState<string>(videoInputD?.deviceId ?? '');
    const [selectedAudioInput, setSelectedAudioInput] = useState<string>(audioInputD?.deviceId ?? '');
    const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>(audioOutputD?.deviceId ?? '');
    const [selectedCaptureBoardInput, setSelectedCaptureBoardInput] = useState<string>(captureBoardD?.deviceId ?? '');
    const [selectedCaptureBoardAudioInput, setSelectedCaptureBoardAudioInput] = useState<string>(
        captureBoardAudioD?.deviceId ?? ''
    );

    const [outputVolume, setOutputVolume] = useState<number>(100);
    const handleOutputVolumeChange = (value: number) => {
        setOutputVolume(value);
        if (window.electronAPI) {
            window.electronAPI.setSpeakerVolume(value);
        }
    };

    const fetchData = async () => {
        await setAllDevices();
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setEditableInfo(centerInfo);
    }, [centerInfo]);

    const handleChange = (key: keyof CenterInfo, value: string) => {
        if (!editableInfo) return;
        setEditableInfo({...editableInfo, [key]: value});
    };

    const checkMediaPermissions = async (): Promise<{camera: boolean; microphone: boolean}> => {
        const result = {camera: false, microphone: false};
        try {
            if (navigator.permissions) {
                const cameraPermission = await navigator.permissions.query({name: 'camera' as PermissionName});
                const micPermission = await navigator.permissions.query({name: 'microphone' as PermissionName});
                result.camera = cameraPermission.state === 'granted';
                result.microphone = micPermission.state === 'granted';
            }
        } catch (error) {
            console.warn('권한 확인 실패:', error);
        }
        return result;
    };

    const handleSave = async () => {
        if (!editableInfo) return;

        const permissions = await checkMediaPermissions();
        if (!permissions.camera && cameraEnable) {
            alert('카메라 권한이 필요합니다. 브라우저 설정을 확인해주세요.');
            return;
        }
        if (!permissions.microphone) {
            alert('마이크 권한이 필요합니다. 브라우저 설정을 확인해주세요.');
            return;
        }

        try {
            await applySelectedDevices();
            setVideoMode(selectedVideoMode);
            setIsEditing(false);
        } catch (error) {
            console.error('설정 저장 실패:', error);
        }
    };

    const handleCancel = () => {
        setEditableInfo(centerInfo);
        setIsEditing(false);
    };

    const applySelectedDevices = async () => {
        try {
            // 기존 스트림 정리
            const currentStream = useCenterStore.getState().mediaStream;
            const currentCaptureStream = useCenterStore.getState().captureBoardMediaStream;

            if (currentStream) {
                currentStream.getTracks().forEach((track) => track.stop());
            }
            if (currentCaptureStream) {
                currentCaptureStream.getTracks().forEach((track) => track.stop());
            }

            // Zustand 상태 저장
            setDatas(
                cameraEnable,
                videoInputA.find((e) => e.deviceId === selectedVideoInput) || videoInputA[0],
                audioInputA.find((e) => e.deviceId === selectedAudioInput) || audioInputA[0],
                audioOutputA.find((e) => e.deviceId === selectedAudioOutput) || audioOutputA[0],
                videoInputA.find((e) => e.deviceId === selectedCaptureBoardInput) || videoInputA[0],
                audioInputA.find((e) => e.deviceId === selectedCaptureBoardAudioInput) || audioInputA[0]
            );

            // Electron에서 오디오 출력 장치 변경
            if (window.electronAPI && selectedAudioOutput) {
                try {
                    const audioOutputDevice = audioOutputA.find((e) => e.deviceId === selectedAudioOutput);
                    await window.electronAPI.setSpeaker(selectedAudioOutput, audioOutputDevice?.label || '');
                    console.log('오디오 출력 장치 변경 성공:', audioOutputDevice?.label);
                } catch (error) {
                    console.error('오디오 출력 장치 변경 실패:', error);
                    // 출력 장치 변경 실패는 치명적이지 않으므로 계속 진행
                }
            }

            if (window.electronAPI && selectedAudioInput) {
                try {
                    const audioInputDevice = audioInputA.find((e) => e.deviceId === selectedAudioInput);
                    await window.electronAPI.setMic(selectedAudioInput, audioInputDevice?.label || '');
                    console.log('오디오 입력 장치 변경 성공:', audioInputDevice?.label);
                } catch (error) {
                    console.error('오디오 입력 장치 변경 실패:', error);
                }
            }

            // 메인 스트림 (카메라 + 마이크)
            const constraints: MediaStreamConstraints = {
                audio: selectedAudioInput ? {deviceId: {exact: selectedAudioInput}} : true,
                ...(cameraEnable &&
                    selectedVideoInput && {
                        video: {deviceId: {exact: selectedVideoInput}},
                    }),
            };

            let stream: MediaStream | null = null;
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
                setMediaStream(stream);
                console.log('메인 스트림 획득 성공:', stream.getTracks());
            } catch (error) {
                console.error('메인 스트림 획득 실패:', error);

                // exact 제약 없이 재시도
                try {
                    const fallbackConstraints: MediaStreamConstraints = {
                        audio: true,
                        ...(cameraEnable && {video: true}),
                    };
                    stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
                    setMediaStream(stream);
                    console.log('폴백 스트림 획득 성공');
                } catch (fallbackError) {
                    console.error('폴백 스트림도 실패:', fallbackError);
                    throw new Error('카메라/마이크 접근 권한이 거부되었습니다. 브라우저 설정에서 권한을 확인해주세요.');
                }
            }

            // 캡처보드 스트림
            const constraintsCaptureBoard: MediaStreamConstraints = {
                audio: selectedCaptureBoardAudioInput
                    ? {
                          deviceId: selectedCaptureBoardAudioInput,
                      }
                    : true,
                video: selectedCaptureBoardInput
                    ? {
                          deviceId: selectedCaptureBoardInput,
                      }
                    : true,
            };

            try {
                const streamCaptureBoard = await navigator.mediaDevices.getUserMedia(constraintsCaptureBoard);
                setCaptureBoardMediaStream(streamCaptureBoard);
                console.log('캡처보드 스트림 획득 성공:', streamCaptureBoard.getTracks());
            } catch (error) {
                console.error('캡처보드 스트림 획득 실패:', error);

                // 캡처보드는 선택사항일 수 있으므로 경고만 표시
                console.warn('캡처보드를 사용할 수 없습니다. 메인 스트림만 사용합니다.');
                setCaptureBoardMediaStream(null);
            }
        } catch (error: any) {
            console.error('장치 적용 중 오류:', error);

            // 사용자에게 알림
            alert(error.message || '미디어 장치 설정 중 오류가 발생했습니다.');

            throw error;
        }
    };

    return (
        <Container>
            <SettingArea>
                <IconArea>
                    <Logo iconWidth={'10vw'}>
                        <img src={LogoSVG} alt={'logo'} style={{marginBottom: '2rem'}} />
                    </Logo>
                </IconArea>
                <Header>
                    <EditableRow>
                        <label>경로당 명</label>
                        <input
                            type="text"
                            value={editableInfo?.name ?? ''}
                            onChange={(e) => handleChange('name', e.target.value)}
                            disabled={!isEditing}
                        />
                    </EditableRow>
                    <EditableRow>
                        <label>경로당 번호</label>
                        <input
                            type="text"
                            value={editableInfo?.center_id ?? ''}
                            onChange={(e) => handleChange('center_id', e.target.value)}
                            disabled={!isEditing}
                        />
                    </EditableRow>
                    <EditableRow>
                        <label>클라이언트 버전</label>
                        <input type="text" value={`${Package.version}.ver`} disabled />
                    </EditableRow>
                    <EditableRow>
                        <label>tv OS 버전</label>
                        <input type="text" value={`${electronVer}.ver`} disabled />
                    </EditableRow>
                    <EditableRow>
                        <label>기기 코드</label>
                        <input type="text" value={deviceID} disabled />
                    </EditableRow>

                    <Partition />
                    <EditableRow>
                        <label>실시간 방송 세팅 : </label>
                    </EditableRow>
                    <DeviceSelector>
                        <label>해상도</label>
                        <select
                            value={selectedVideoMode}
                            onChange={(e) => setSelectedVideoMode(e.target.value as IVideoMode)}
                            disabled={!isEditing}
                        >
                            <option key={'best-perform'} value={'best-perform'}>
                                최적 성능
                            </option>
                            <option key={'high-quality'} value={'high-quality'}>
                                고화질
                            </option>
                        </select>
                    </DeviceSelector>
                    <ToggleContainer>
                        <label>카메라 전원</label>
                        <ToggleButton $enabled={cameraEnable} onClick={setToggleCameraEnable} />
                    </ToggleContainer>
                    <DeviceSelector>
                        <label>카메라</label>
                        <select
                            value={selectedVideoInput}
                            onChange={(e) => setSelectedVideoInput(e.target.value)}
                            disabled={!isEditing}
                        >
                            {videoInputA.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                    </DeviceSelector>
                    <DeviceSelector>
                        <label>마이크</label>
                        <select
                            value={selectedAudioInput}
                            onChange={(e) => setSelectedAudioInput(e.target.value)}
                            disabled={!isEditing}
                        >
                            {audioInputA.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                    </DeviceSelector>

                    <Partition />
                    <EditableRow>
                        <label>ip-tv 세팅 : </label>
                    </EditableRow>
                    <DeviceSelector>
                        <label>ip-tv 비디오 입력 장치</label>
                        <select
                            value={selectedCaptureBoardInput}
                            onChange={(e) => setSelectedCaptureBoardInput(e.target.value)}
                            disabled={!isEditing}
                        >
                            {videoInputA.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                    </DeviceSelector>
                    <DeviceSelector>
                        <label>ip-tv 오디오 입력 장치</label>
                        <select
                            value={selectedCaptureBoardAudioInput}
                            onChange={(e) => setSelectedCaptureBoardAudioInput(e.target.value)}
                            disabled={!isEditing}
                        >
                            {audioInputA.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                    </DeviceSelector>

                    <Partition />
                    <EditableRow>
                        <label>스피커 세팅 : </label>
                    </EditableRow>
                    <DeviceSelector>
                        <label>스피커</label>
                        <select
                            value={selectedAudioOutput}
                            onChange={(e) => setSelectedAudioOutput(e.target.value)}
                            disabled={!isEditing}
                        >
                            {audioOutputA.map((d) => (
                                <option key={d.deviceId} value={d.deviceId}>
                                    {d.label}
                                </option>
                            ))}
                        </select>
                    </DeviceSelector>
                    <DeviceSelector>
                        <VolumeLabel>
                            <span>스피커 볼륨 </span>
                            <span>{outputVolume}%</span>
                        </VolumeLabel>
                        <VolumeSlider
                            type="range"
                            min="0"
                            max="100"
                            value={outputVolume}
                            onChange={(e) => handleOutputVolumeChange(Number(e.target.value))}
                            disabled={!isEditing}
                        />
                    </DeviceSelector>
                </Header>

                <ButtonGroup>
                    {isEditing ? (
                        <>
                            <Button onClick={handleSave}>저장</Button>
                            <Button onClick={handleCancel} $cancel={true}>
                                취소
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => setIsEditing(true)}>수정</Button>
                    )}
                </ButtonGroup>
            </SettingArea>
        </Container>
    );
};

export default InformationSetting;
